import { CALENDAR_DATA, FIRST_DAY_OF_MONTH } from './data';

export const parseDateString = (dateString: string) => {
  return dateString.split('-').map((str) => parseInt(str, 10));
};

export const isValidDateString = (dateString: string) => {
  // Check if dateString is a string and matches the format "yyyy-mm-dd"
  if (
    typeof dateString !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(dateString)
  ) {
    return false; // Return false for invalid formats
  }
  const [year, month, day] = parseDateString(dateString);
  return isValidDate(year, month, day);
};

export const isValidDate = (year: number, month: number, day: number) => {
  if (year && month && day) {
    const isValidYear = Boolean(CALENDAR_DATA[year]);
    const isValidMonth = month > 0 && month < 13;
    const isValidDate =
      isValidYear && isValidMonth && day <= CALENDAR_DATA[year][month - 1];
    return isValidDate && isValidMonth && isValidDate;
  }
  return false;
};

export const getMonthData = (year: number, month: number) => {
  return {
    totalDays: year < 1970 ? undefined : CALENDAR_DATA[year][month - 1],
    startDay: year < 1970 ? undefined : FIRST_DAY_OF_MONTH[year][month - 1],
  };
};

export const formatDate = (year: number, month: number, day: number) => {
  const formattedMonth = month.toString().padStart(2, '0');
  const formattedDate = day.toString().padStart(2, '0');

  return `${year}-${formattedMonth}-${formattedDate}`;
};

export const isBetween = (
  date: string,
  startDate?: string,
  endDate?: string,
) => {
  // Check if current date string is valid
  if (!isValidDateString(date)) {
    return false; // Return false if date string is invalid
  }

  const [year, month, day] = parseDateString(date);

  if (!startDate) {
    // Case: startDate is undefined
    if (!endDate || !isValidDateString(endDate)) {
      return true; // If endDate is not specified or invalid, always return true
    }

    const [endYear, endMonth, endDay] = parseDateString(endDate);

    // Check if the current date is before the endDate
    const isBeforeEnd =
      year < endYear ||
      (year === endYear && month < endMonth) ||
      (year === endYear && month === endMonth && day <= endDay);

    return isBeforeEnd;
  }

  if (!endDate) {
    // Case: endDate is undefined
    if (!startDate || !isValidDateString(startDate)) {
      return true; // If startDate is not specified or invalid, always return true
    }

    const [startYear, startMonth, startDay] = parseDateString(startDate);

    // Check if the current date is after the startDate
    const isAfterStart =
      year > startYear ||
      (year === startYear && month > startMonth) ||
      (year === startYear && month === startMonth && day >= startDay);

    return isAfterStart;
  }

  const [startYear, startMonth, startDay] = parseDateString(startDate);
  const [endYear, endMonth, endDay] = parseDateString(endDate);

  // Check if the given date is after the start date and before the end date
  const isAfterStart =
    year > startYear ||
    (year === startYear && month > startMonth) ||
    (year === startYear && month === startMonth && day >= startDay);
  const isBeforeEnd =
    year < endYear ||
    (year === endYear && month < endMonth) ||
    (year === endYear && month === endMonth && day <= endDay);

  return isAfterStart && isBeforeEnd;
};

export const toDevanagari = (number: number): string => {
  const devanagariDigits: { [key: string]: string } = {
    '0': '०',
    '1': '१',
    '2': '२',
    '3': '३',
    '4': '४',
    '5': '५',
    '6': '६',
    '7': '७',
    '8': '८',
    '9': '९',
  };

  if (number < 1 || number > 32) {
    return number.toString();
  }

  return number
    .toString()
    .split('')
    .map((digit) => devanagariDigits[digit])
    .join('');
};
